import {
  PIPE_STRAP_BRANCH,
  PIPE_STRAP_RISER,
  PIPE_STRAP_DECLINED
} from '../../util/selective-pipe-strap'

// The pipe-placement question, moved out of the after-setup questionnaire so
// it's answered at the install spot before the probe is plugged in — and so
// the naming step can arrive with the matching name already selected. The
// phone has no internet here (it's joined to the sensor's setup network), so
// the answer is held on the onboarding instance and submitted by the
// after-setup questionnaire step once connectivity is back.
export class StepConnectedPipeQuestion {
  constructor (onboarding) {
    this.onboarding = onboarding;
  }

  id = "connected_pipe_question";
  title = "Sensor Setup";

  help_open = false;

  template = `
  <div class="onboarding-step" data-for="connected_pipe_question">

    <div class="onboarding-instruction__view" data-view="main">
      <div class="onboarding-selective-questions">
        <div class="onboarding-selective-questions__card">
          <div class="onboarding-selective-questions__title">
            Where will this Temp Stick Pipe Sensor be attached?
          </div>

          <div class="onboarding-selective-questions__hero-options">
            <button
              type="button"
              class="onboarding-selective-questions__hero-option"
              data-for="pipe_strap_select"
              data-value="${PIPE_STRAP_BRANCH}"
            >
              <div class="onboarding-selective-questions__hero-image">
                <img src="${window.site.home}/../assets/images/setup-pipe-strap-branch.png" alt="Attached to a Branch Pipe" />
              </div>
              <div class="onboarding-selective-questions__hero-label">
                Attached to a Branch Pipe
              </div>
              <div class="onboarding-selective-questions__hero-tip">A horizontal pipe near an exterior wall is preferred.</div>
            </button>
            <button
              type="button"
              class="onboarding-selective-questions__hero-option"
              data-for="pipe_strap_select"
              data-value="${PIPE_STRAP_RISER}"
            >
              <div class="onboarding-selective-questions__hero-image">
                <img src="${window.site.home}/../assets/images/setup-pipe-strap-riser.png" alt="Attached to a Riser Pipe" />
              </div>
              <div class="onboarding-selective-questions__hero-label">
                Attached to a Riser Pipe
              </div>
              <div class="onboarding-selective-questions__hero-tip">A large vertical water supply pipe in a riser room.</div>
            </button>
          </div>

          <button
            type="button"
            class="onboarding-selective-questions__help-link"
            data-for="pipe_strap_open_help"
          >
            Help me tell branch pipes from riser pipes
          </button>

          <button
            type="button"
            class="onboarding-selective-questions__decline-link"
            data-for="pipe_strap_decline"
          >
            Not using the pipe sensor
          </button>
        </div>
      </div>
    </div>

    <div class="onboarding-instruction__view" data-view="help">
      <div class="onboarding-selective-questions">
        <div class="onboarding-selective-questions__card onboarding-selective-questions__card--help">
          <div class="onboarding-selective-questions__title">
            Branch pipes vs. riser pipes
          </div>

          <div class="onboarding-selective-questions-help">
            <div class="onboarding-selective-questions-help__item">
              <div class="onboarding-selective-questions-help__heading">Branch pipe</div>
              <div class="onboarding-selective-questions-help__text">Branch pipes are the smaller pipes (typically 1&quot;&ndash;2&quot;) that run horizontally overhead and feed the individual sprinkler heads.</div>
              <div class="onboarding-selective-questions-help__text">They hold less water and often pass through attics, above ceilings, and along exterior walls &mdash; so they freeze and can burst more quickly than riser pipes.</div>
            </div>

            <div class="onboarding-selective-questions-help__item">
              <div class="onboarding-selective-questions-help__heading">Riser pipe</div>
              <div class="onboarding-selective-questions-help__text">The riser is the large vertical main (often 4&quot; or bigger) that carries water from the supply into the system &mdash; usually in the riser room, next to the alarm valve and gauges.</div>
              <div class="onboarding-selective-questions-help__text">Risers hold more water and freeze more slowly, but a frozen riser can put the entire system out of service.</div>
            </div>

            <div class="onboarding-selective-questions-help__item">
              <div class="onboarding-selective-questions-help__heading">How to tell them apart</div>
              <div class="onboarding-selective-questions-help__text">Follow the water: the riser is the biggest pipe in the system and runs vertically where water enters the building. Branch pipes are the smallest pipes &mdash; the last run before each sprinkler head.</div>
            </div>

            <div class="onboarding-selective-questions-help__item">
              <div class="onboarding-selective-questions-help__heading">Monitoring best practices</div>
              <div class="onboarding-selective-questions-help__text">Attach the sensor to a branch pipe near an exterior wall or in the coldest space it passes through (attic, loading dock, parking garage) &mdash; that section freezes first and gives you the earliest warning.</div>
              <div class="onboarding-selective-questions-help__text">Set a low-temperature alert around 40&deg;F / 4&deg;C so you have time to act before the water in the pipe reaches freezing.</div>
              <div class="onboarding-selective-questions-help__text">During cold snaps, watch for steady temperature drops &mdash; they can reveal failing heat, missing insulation, or drafts near the pipe before a freeze happens.</div>
              <div class="onboarding-selective-questions-help__text">While attaching the sensor, check the pipe for corrosion, leaks, or damaged insulation &mdash; defects tend to show up first at the coldest points.</div>
            </div>

            <div class="onboarding-selective-questions-help__item">
              <div class="onboarding-selective-questions-help__heading">Still not sure?</div>
              <div class="onboarding-selective-questions-help__text">Ask your fire safety inspector or sprinkler contractor &mdash; they can point out the riser and branch pipes in your building.</div>
            </div>
          </div>

          <div class="onboarding-selective-questions__actions">
            <button
              type="button"
              class="button button--grey-outline"
              data-for="pipe_strap_close_help"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
  `;

  onBack = () => {

    if (this.help_open) {
      this.showQuestion();
      return;
    }

    this.onboarding.goToStep(this.onboarding.stepBeforePipeQuestion());

  }

  onActivate = () => {
    this.onboarding.showBackButton();
    this.showQuestion();
  }

  showQuestion = () => {
    this.help_open = false;
    this.$mainView.addClass('is-active');
    this.$helpView.removeClass('is-active');
  }

  showHelp = () => {
    this.help_open = true;
    this.$mainView.removeClass('is-active');
    this.$helpView.addClass('is-active');
  }

  answer = value => {
    this.onboarding.pipe_strap_location = value;
    this.onboarding.goToStepAfterPipeQuestion();
  }

  bindActions = () => {

    const $step = $(this.onboarding.container).find('.onboarding-step[data-for="connected_pipe_question"]');

    this.$mainView = $step.find('[data-view="main"]');
    this.$helpView = $step.find('[data-view="help"]');

    $step.on('click', '[data-for="pipe_strap_select"]', e => {
      const value = String($(e.currentTarget).attr('data-value') || '');

      if (value !== PIPE_STRAP_BRANCH && value !== PIPE_STRAP_RISER) {
        return;
      }

      this.answer(value);
    });

    $step.on('click', '[data-for="pipe_strap_decline"]', () => {
      this.answer(PIPE_STRAP_DECLINED);
    });

    $step.on('click', '[data-for="pipe_strap_open_help"]', () => {
      this.showHelp();
    });

    $step.on('click', '[data-for="pipe_strap_close_help"]', () => {
      this.showQuestion();
    });

  }

}
